import { getProfile, user } from '$lib/user';
import { profileTable, setSupabaseAuthJWT } from '$lib/supabase';
import { createParaViemClient, createParaAccount } from '@getpara/viem-v2-integration';
import { selectOrInsertProfile } from '$lib/user/para/utils';
import { goto } from '$app/navigation';
import { showParaModal } from './store';
import { createWalletClient, http, publicActions } from 'viem';
// , type Chain
import { optimism, base, optimismSepolia } from 'viem/chains';
import { walletBalance } from '../wallet.store';
import { get } from 'svelte/store';

export const paraLogout = async () => {
	const { paraClient } = await import('$lib/user/para/client');
	const exportedSession = paraClient.exportSession();
	const response = await fetch('/api/paraauth', {
		method: 'DELETE',
		headers: {
			'Content-Type': 'application/json'
		},
		body: JSON.stringify({
			paraSession: exportedSession
		})
	});
	user.set(undefined);
	setSupabaseAuthJWT(null);
	showParaModal.set(false);

	if (!response.ok) {
		console.log('Para logout failed:', response.statusText);
	}

	goto('/');
};

export const paraLogin = async () => {
	const { paraClient } = await import('$lib/user/para/client');
	showParaModal.set(true);

	await new Promise<void>((res) => {
		showParaModal.subscribe((value) => {
			if (value === false) {
				res();
			}
		});
	});

	try {
		const isLoggedIn = await paraClient.isFullyLoggedIn();

		if (!isLoggedIn) {
			return false;
		}

		const wallets = await paraClient.fetchWallets();
		const walletAddress: string = Object.values(wallets)[0].address!;
		const email = paraClient.getEmail();

		if (!walletAddress) {
			throw new Error('No wallet address found');
		}

		if (!email) {
			throw new Error('No email found');
		}

		const {
			id: userId,
			member,
			wallet: userWallet
		} = await selectOrInsertProfile(email, walletAddress);
		const exportedSession = await paraClient.exportSession();

		const response = await fetch('/api/paraauth', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({
				paraSession: exportedSession
			})
		});

		const { jwt } = await response.json();
		setSupabaseAuthJWT(jwt);

		// TODO: insert wallet into the user's profile record here - if data but not wallet, insert wallet into profile record
		if (!userWallet) {
			console.log('no wallet in profile', userWallet);
			const { error: updateError } = await profileTable('profiles')
				.update({ wallet: walletAddress })
				.eq('id', userId);

			if (updateError) {
				console.log(updateError);
				throw new Error('Failed to update wallet in profile');
			}
		}

		user.set({
			walletAddress,
			email,
			id: userId,
			member
		});

		await getProfile();

		return true;
	} catch (err) {
		console.error('Para login status check failed:', err);
		return false;
	}
};

// Create the viem client for interacting with the blockchain
// const viemClient = createWalletClient({
// 	chain: optimism,
// 	transport: http('https://mainnet.optimism.io')
// }).extend(publicActions);

// Create viem clients for both chains
const optimismClient = createWalletClient({
	chain: optimism,
	transport: http('https://mainnet.optimism.io')
}).extend(publicActions);

const baseClient = createWalletClient({
	chain: base,
	transport: http('https://mainnet.base.org')
}).extend(publicActions);

// Create a Sepolia client
const sepoliaClient = createWalletClient({
	chain: optimismSepolia,
	transport: http('https://sepolia.optimism.io')
}).extend(publicActions);

// const sepolia = {
// 	id: 11155111, // Sepolia's chain ID
// 	name: 'Sepolia',
// 	nativeCurrency: {
// 		name: 'Sepolia Ether',
// 		symbol: 'ETH',
// 		decimals: 18
// 	},
// 	rpcUrls: {
// 		default: {
// 			http: ['https://sepolia.infura.io/v3/YOUR_INFURA_PROJECT_ID'] // Replace with your RPC URL
// 		}
// 	}
// };

// Example function to get balance on Sepolia
export const getSepoliaBalance = async (userAddress: string) => {
	try {
		const rawSepoliaEthBalance = await sepoliaClient.getBalance({
			address: userAddress as `0x${string}`
		});
		const sepoliaEthBalance = (
			Math.floor(parseFloat(rawSepoliaEthBalance.toString()) / Math.pow(10, 13)) / Math.pow(10, 5)
		).toString();

		console.log('Sepolia ETH Balance:', sepoliaEthBalance);
		return sepoliaEthBalance;
	} catch (error) {
		console.error('Error fetching Sepolia balance:', error);
		return 'Error';
	}
};

// get iUSD balance
async function getSepoliaIUSDBalance(userAddress: string): Promise<string> {
	try {
		const iUSDAddress = tokenAddresses.optimismSepolia.iUSD;
		const balanceRaw = await sepoliaClient.call({
			to: iUSDAddress,
			data: `0x70a08231000000000000000000000000${userAddress.slice(2).toLowerCase()}`
		});
		return (parseInt((balanceRaw.data ?? '0x0').slice(2), 16) / Math.pow(10, 18)).toString();
	} catch (error) {
		console.error('Error fetching iUSD balance:', error);
		return '0';
	}
}

export const getWalletBalances = async (userAddress: string) => {
	try {
		// console.log('Fetching balances for account:', userAddress);

		// Optimism balances
		const rawOptimismEthBalance = await optimismClient.getBalance({
			address: userAddress as `0x${string}`
		});
		const optimismEthBalance = (
			Math.floor(parseFloat(rawOptimismEthBalance.toString()) / Math.pow(10, 13)) / Math.pow(10, 5)
		).toString();

		const optimismUsdcAddress = '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85';
		const optimismUsdcBalanceRaw = await optimismClient.call({
			to: optimismUsdcAddress,
			data: `0x70a08231000000000000000000000000${userAddress.slice(2).toLowerCase()}`
		});
		const optimismUsdcBalance = (
			parseInt(optimismUsdcBalanceRaw.toString().slice(2), 16) / Math.pow(10, 6)
		).toString();

		const bloomAddress = '0x4Ab1BFC688992e7c6C344d0a31C5CBd6FbD86f33';
		const bloomBalanceRaw = await optimismClient.call({
			to: bloomAddress,
			data: `0x70a08231000000000000000000000000${userAddress.slice(2).toLowerCase()}`
		});
		// console.log('Raw Bloom balance response:', bloomBalanceRaw);

		const bloomBalance = (
			parseInt((bloomBalanceRaw.data ?? '0x0').slice(2), 16) / Math.pow(10, 18)
		).toString();
		// console.log('Formatted Bloom balance:', bloomBalance);

		// Base balances
		const rawBaseEthBalance = await baseClient.getBalance({
			address: userAddress as `0x${string}`
		});
		const baseEthBalance = (
			Math.floor(parseFloat(rawBaseEthBalance.toString()) / Math.pow(10, 13)) / Math.pow(10, 5)
		).toString();

		const baseUsdcAddress = '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913';
		const baseUsdcBalanceRaw = await baseClient.call({
			to: baseUsdcAddress,
			data: `0x70a08231000000000000000000000000${(userAddress as `0x${string}`).slice(2).toLowerCase()}`
		});

		const rawValue = BigInt(baseUsdcBalanceRaw.data ?? '0x0');
		const baseUsdcBalance = (Number(rawValue) / 1_000_000).toString();
		// console.log('Raw hex:', baseUsdcBalanceRaw.data);
		// console.log('Raw decimal:', rawValue.toString());
		// console.log('Formatted USDC:', baseUsdcBalance);

		// Update the wallet balance store
		walletBalance.set({
			optimism: {
				ethBalance: optimismEthBalance,
				usdcBalance: optimismUsdcBalance,
				bloomBalance: bloomBalance
			},
			base: {
				ethBalance: baseEthBalance,
				usdcBalance: baseUsdcBalance
			}
		});
		// console.log('Store updated with new balances');
	} catch (error) {
		console.error('Error fetching wallet balances:', error);
		walletBalance.set({
			optimism: {
				ethBalance: 'Error',
				usdcBalance: 'Error',
				bloomBalance: 'Error'
			},
			base: {
				ethBalance: 'Error',
				usdcBalance: 'Error'
			}
		});
	}
};

//SEND USING VIEM SETUP INSTRUCTIONS

// Converts Ether value to wei
export function parseEther(value: string): bigint {
	return BigInt(Math.floor(parseFloat(value) * 1e18));
}

// Token addresses with proper typing
const tokenAddresses = {
	optimism: {
		USDC: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85',
		FLO: '0x4Ab1BFC688992e7c6C344d0a31C5CBd6FbD86f33'
	} as const,
	base: {
		USDC: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913'
	} as const,
	optimismSepolia: {
		iUSD: '0x065775C7aB4E60ad1776A30DCfB15325d231Ce4F'
	} as const
} as const;

// Add this before sendTransaction function
const displayToBlockchainSymbol = {
	'ETH on Base': 'ETH',
	'ETH on Optimism': 'ETH',
	'FLO Token on Optimism': 'FLO',
	'USDC on Base': 'USDC',
	'USDC on Optimism': 'USDC',
	iUSD: 'iUSD',
	FLO: 'FLO'
} as const;

// Function to send a transaction
export const sendTransaction = async (
	recipient: string,
	amount: string,
	selectedChain: typeof optimism | typeof base | typeof optimismSepolia,
	displaySymbol: string
): Promise<string> => {
	const { paraClient } = await import('$lib/user/para/client');
	// Check authentication first
	const isLoggedIn = await paraClient.isFullyLoggedIn();
	if (!isLoggedIn) {
		const loginSuccess = await paraLogin();
		if (!loginSuccess) {
			throw new Error('Failed to reconnect to Para. Please try again.');
		}
	}

	const tokenSymbol =
		displayToBlockchainSymbol[displaySymbol as keyof typeof displayToBlockchainSymbol];

	if (!recipient || !amount) {
		throw new Error('Recipient address and amount are required.');
	}
	if (!/^0x[a-fA-F0-9]{40}$/.test(recipient)) {
		throw new Error('Invalid recipient address.');
	}
	if (!tokenSymbol) {
		throw new Error('Token symbol is required');
	}

	const paraAccount = await createParaAccount(paraClient);

	// Check token balance before proceeding
	await getWalletBalances(paraAccount.address);
	const walletBalances = get(walletBalance);

	// Get current balance based on chain and token
	let currentBalance = '0';
	if (selectedChain.id === optimism.id) {
		if (displaySymbol === 'FLO') {
			currentBalance = walletBalances.optimism.bloomBalance;
		} else if (displaySymbol === 'USDC on Optimism') {
			currentBalance = walletBalances.optimism.usdcBalance;
		} else if (displaySymbol === 'ETH on Optimism') {
			currentBalance = walletBalances.optimism.ethBalance;
		}
	} else if (selectedChain.id === base.id) {
		if (displaySymbol === 'USDC on Base') {
			currentBalance = walletBalances.base.usdcBalance;
		} else if (displaySymbol === 'ETH on Base') {
			currentBalance = walletBalances.base.ethBalance;
		}
	} else if (selectedChain.id === optimismSepolia.id) {
		if (displaySymbol === 'iUSD') {
			currentBalance = await getSepoliaIUSDBalance(paraAccount.address);
		}
	}

	// Check token balance with clear error message
	if (Number(currentBalance) < Number(amount)) {
		throw new Error(
			`Insufficient ${displaySymbol} balance. You have ${currentBalance} ${displaySymbol} but the transaction requires ${amount} ${displaySymbol}.`
		);
	}

	const paraViemSigner = createParaViemClient(paraClient, {
		account: paraAccount,
		chain: selectedChain,
		transport: http(
			selectedChain.id === optimism.id
				? 'https://mainnet.optimism.io'
				: selectedChain.id === optimismSepolia.id
					? 'https://sepolia.optimism.io'
					: 'https://mainnet.base.org'
		)
	});

	// Prepare the transaction object
	try {
		if (tokenSymbol === 'ETH') {
			// Native ETH transfer
			const transaction = {
				account: paraAccount,
				chain: selectedChain,
				to: recipient as `0x${string}`,
				value: parseEther(amount),
				gas: BigInt(21000)
			};

			const txHash = await paraViemSigner.sendTransaction(transaction);

			await getWalletBalances(paraAccount.address);

			return txHash;
		} else {
			// ERC20 token transfer
			let tokenAddress: string | undefined;

			if (selectedChain.id === optimism.id) {
				tokenAddress = tokenAddresses.optimism[tokenSymbol as keyof typeof tokenAddresses.optimism];
				// console.log('Debug - Optimism token address:', tokenAddress);
			} else if (selectedChain.id === base.id) {
				tokenAddress = tokenAddresses.base[tokenSymbol as keyof typeof tokenAddresses.base];
				// console.log('Debug - Base token address:', tokenAddress);
			} else if (selectedChain.id === optimismSepolia.id) {
				tokenAddress =
					tokenAddresses.optimismSepolia[
						tokenSymbol as keyof typeof tokenAddresses.optimismSepolia
					];
			}

			if (!tokenAddress) {
				throw new Error(`Token ${tokenSymbol} not supported on ${selectedChain.name}`);
			}

			// Create ERC20 transfer data
			const decimals = tokenSymbol === 'USDC' ? 6 : 18;
			const value = BigInt(Math.floor(parseFloat(amount) * Math.pow(10, decimals)));
			const data = `0xa9059cbb${recipient.slice(2).padStart(64, '0')}${value.toString(16).padStart(64, '0')}`;

			const transaction = {
				account: paraAccount,
				chain: selectedChain,
				to: tokenAddress as `0x${string}`,
				data: data as `0x${string}`,
				gas: BigInt(100000)
			};

			// console.log('Debug - Transaction:', transaction);
			const txHash = await paraViemSigner.sendTransaction(transaction);

			// Add balance refresh after successful transaction
			await getWalletBalances(paraAccount.address);

			return txHash;
		}
	} catch (error) {
		console.error('Error signing or sending transaction:', error);
		if (error.message?.includes('WebSocket')) {
			throw new Error('Connection to wallet lost. Please refresh and try again.');
		}
		throw new Error(error.message || 'Transaction failed');
	}
};
